/**
 * Settings' People (plan §F.5.5, step F2): who is in this business and what
 * each may do. Everyone sees the list; an admin changes it.
 *
 * WHAT EACH ROLE MEANS is said once, in an InfoNote, rather than on every row:
 * an admin does everything, a bookkeeper categorizes and reconciles, a viewer
 * reads. Adjust Access shows, for a bookkeeper or a viewer, every kind of
 * access with where it comes from (the role, or a grant or a withdrawal), and
 * each change is saved as it is made. The database refuses what would leave
 * the business without an admin, and the screen says the server's sentence.
 *
 * REMOVING A PERSON uses the Matching screen's inline confirmation. Their
 * sessions end at once and everything they did stays theirs; if this was
 * their last business, their sign-in is deleted (0071), which the notice says.
 *
 * INVITING A PERSON (step F3, migration 0073) is an admin's: their address and
 * a role, and the screen shows the link ONCE, for the admin to send by their
 * own means -- nothing in this application sends email, and the code is
 * stored only as a hash. The open invitations are listed without their codes,
 * each with Revoke. The person joins at `/join` (JoinPage.tsx): with the
 * password of a sign-in the address already has, or a new one.
 *
 * A change to the signed-in person's own role or access re-reads the session,
 * so the screens offer what they now may do without a reload.
 *
 * Read on its own, as Business Details is, so a failure here leaves the rest
 * of Settings working.
 */

import { useEffect, useState } from 'react';
import { ApiError, api, type Capability, type MemberInvite, type Person, type Role } from '../api.js';
import { keepsNetAssets, useAuth, useBusiness } from '../auth/AuthProvider.js';
import { formatTimestamp } from '../lib/timezone.js';
import { InfoNote } from './InfoNote.js';
import { Toast } from './Toast.js';

const ROLE_LABELS: Record<Role, string> = { admin: 'Admin', bookkeeper: 'Bookkeeper', viewer: 'Viewer' };

/** Every kind of access a bookkeeper or a viewer may be given, in the operator's words. */
const ACCESS: Array<{ capability: Capability; label: string }> = [
  { capability: 'edit_entries', label: 'Edit Entries' },
  { capability: 'reconcile', label: 'Reconcile' },
  { capability: 'close_periods', label: 'Close Periods' },
  { capability: 'view_payroll', label: 'See Payroll' },
  { capability: 'run_payroll', label: 'Run Payroll' },
  { capability: 'view_tax_ids', label: 'See Tax IDs' },
  { capability: 'manage_banks', label: 'Manage Bank Connections' },
  { capability: 'manage_business', label: 'Change The Business And Its Chart' },
  { capability: 'export_data', label: "Export The Business's Data" },
  // 0084 (§B.17): a non-profit's giving report and year-end letters.
  { capability: 'view_donors', label: 'See Donor Giving' },
];

const ROLE_DEFAULTS: Record<Exclude<Role, 'admin'>, Capability[]> = {
  bookkeeper: ['edit_entries', 'reconcile'],
  viewer: [],
};

type Choice = 'role' | 'allow' | 'deny';

function message(caught: unknown, fallback: string): string {
  return caught instanceof ApiError && caught.message !== caught.code ? caught.message : fallback;
}

export function PeopleSection() {
  const { session, can, refresh } = useAuth();
  const manages = can('manage_users');
  // 0084: donor giving is a non-profit's, so only a non-profit is offered its grant.
  const nonprofit = keepsNetAssets(useBusiness());
  const [people, setPeople] = useState<Person[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adjusting, setAdjusting] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  // Bumped by each change, so Invitations reads again: removing or demoting an
  // admin withdraws the invitations they made (0073, review IV-1).
  const [changes, setChanges] = useState(0);

  async function load() {
    try {
      setPeople((await api.listPeople()).people);
      setLoadError(null);
    } catch (caught) {
      setLoadError(message(caught, 'Could not read the people of this business.'));
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function act(userId: string, work: () => Promise<unknown>, done?: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await work();
      await load();
      setChanges((n) => n + 1);
      if (userId === session?.userId) await refresh();
      if (done) setNotice(done);
    } catch (caught) {
      setError(message(caught, 'That was not saved.'));
    } finally {
      setBusy(false);
    }
  }

  const choiceOf = (person: Person, capability: Capability): Choice => {
    const adjustment = person.adjustments.find((a) => a.capability === capability);
    return adjustment === undefined ? 'role' : adjustment.allowed ? 'allow' : 'deny';
  };

  const active = people?.filter((person) => person.removedAt === null) ?? [];
  const removed = people?.filter((person) => person.removedAt !== null) ?? [];

  return (
    <section className="settings-section" aria-labelledby="settings-people">
      <h2 id="settings-people">People</h2>
      <p className="muted">
        Who is in this business, and what each may do.{' '}
        <InfoNote label="What Each Role Means">
          An admin may do everything, including changing who may do what. A bookkeeper may categorize, post and match
          entries, import bank files and reconcile, and cannot see payroll, close periods, change bank connections or
          export the business&apos;s data unless an admin allows it. A viewer may read the books and change nothing.
          An admin can give a bookkeeper or a viewer any of these, or take one away, under Adjust Access. Managing
          people is what an admin does, so it is given by making someone an admin.
          {nonprofit && (
            <>
              {' '}
              See Donor Giving opens the giving report and the year-end letters. It does not hide a gift on the entry
              that recorded it: anyone who may read the books sees each gift&apos;s amount and who gave it.
            </>
          )}
        </InfoNote>
      </p>

      {notice && !error && <Toast message={notice} variant="ok" onDismiss={() => setNotice(null)} />}
      {loadError && (
        <p role="alert" className="error">
          {loadError}
        </p>
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {people !== null && (
        <div className="table-scroll">
          <table className="lines">
            <thead>
              <tr>
                <th>Person</th>
                <th>Role</th>
                <th>Last Signed In</th>
                {manages && <th aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {active.map((person) => {
                const you = person.userId === session?.userId;
                return (
                  <tr key={person.userId}>
                    <td>
                      {person.email}
                      {you && <span className="tag">You</span>}
                    </td>
                    <td>
                      {manages ? (
                        <select
                          aria-label={`Role for ${person.email}`}
                          value={person.role}
                          disabled={busy}
                          onChange={(event) => {
                            const value = event.target.value as Role;
                            void act(person.userId, () => api.setPersonRole(person.userId, value), `${person.email}: ${ROLE_LABELS[value]}.`);
                          }}
                        >
                          {(Object.keys(ROLE_LABELS) as Role[]).map((role) => (
                            <option key={role} value={role}>
                              {ROLE_LABELS[role]}
                            </option>
                          ))}
                        </select>
                      ) : (
                        ROLE_LABELS[person.role]
                      )}
                      {person.adjustments.length > 0 && <span className="muted"> (adjusted)</span>}
                    </td>
                    <td>{person.lastSignInAt ? formatTimestamp(person.lastSignInAt) : 'Never'}</td>
                    {manages && (
                      <td>
                        <span className="row">
                          {person.role !== 'admin' && (
                            <button
                              type="button"
                              className="link"
                              onClick={() => {
                                setRemoving(null);
                                setAdjusting(adjusting === person.userId ? null : person.userId);
                              }}
                            >
                              Adjust Access
                            </button>
                          )}
                          {!you && (
                            <button
                              type="button"
                              className="link"
                              onClick={() => {
                                setAdjusting(null);
                                setRemoving(person.userId);
                              }}
                            >
                              Remove
                            </button>
                          )}
                        </span>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {active
        .filter((person) => person.userId === adjusting && person.role !== 'admin')
        .map((person) => (
          <div key={person.userId} className="new-account people-access" role="group" aria-label={`Access for ${person.email}`}>
            <p>
              What {person.email} may do beyond reading the books. As The Role follows the{' '}
              {ROLE_LABELS[person.role].toLowerCase()} role; each change is saved as it is made.
            </p>
            {ACCESS.filter(({ capability }) => capability !== 'view_donors' || nonprofit).map(({ capability, label }) => {
              const fromRole = ROLE_DEFAULTS[person.role as Exclude<Role, 'admin'>].includes(capability);
              const allowed = person.capabilities.includes(capability);
              return (
                <label key={capability} className="field">
                  <span>
                    {label} <span className="muted">({allowed ? 'Allowed' : 'Not Allowed'})</span>
                  </span>
                  <select
                    aria-label={`${label} for ${person.email}`}
                    value={choiceOf(person, capability)}
                    disabled={busy}
                    onChange={(event) => {
                      const choice = event.target.value as Choice;
                      void act(person.userId, () =>
                        api.setPersonAccess(person.userId, capability, choice === 'role' ? null : choice === 'allow'),
                      );
                    }}
                  >
                    <option value="role">As The Role ({fromRole ? 'Yes' : 'No'})</option>
                    <option value="allow">Allow</option>
                    <option value="deny">Do Not Allow</option>
                  </select>
                </label>
              );
            })}
            <div className="row">
              <button type="button" className="link" onClick={() => setAdjusting(null)}>
                Done
              </button>
            </div>
          </div>
        ))}

      {active
        .filter((person) => person.userId === removing)
        .map((person) => (
          <div key={person.userId} className="undo-form" role="group" aria-label={`Remove ${person.email}`}>
            <p>
              {person.email} is signed out at once and can no longer open this business. Everything they did stays in
              the books under their name. If this is the last business they are in, their password, authenticator and
              recovery codes are deleted, and any invitation they made that is still open is withdrawn. This is not
              undone; they can be invited again later.
            </p>
            <div className="row">
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void act(person.userId, async () => {
                    const result = await api.removePerson(person.userId);
                    setRemoving(null);
                    setNotice(
                      result.signInErased
                        ? `${person.email} was removed, and their sign-in deleted.`
                        : `${person.email} was removed. They still sign in to their other businesses.`,
                    );
                  })
                }
              >
                {busy ? 'Removing…' : 'Remove'}
              </button>
              <button type="button" className="link" onClick={() => setRemoving(null)}>
                Cancel
              </button>
            </div>
          </div>
        ))}

      {removed.length > 0 && (
        <p className="muted">
          Removed: {removed.map((person) => `${person.email} (${formatTimestamp(person.removedAt!)})`).join(', ')}.
        </p>
      )}

      {manages && <Invitations changed={changes} />}
    </section>
  );
}

/** The link a person joins by. The code is in it, so it is shown once and never stored here. */
export function joinLink(code: string): string {
  return `${window.location.origin}/join?code=${encodeURIComponent(code)}`;
}

/** Inviting a person, and the invitations still open (an admin's). */
function Invitations({ changed }: { changed: number }) {
  const [invites, setInvites] = useState<MemberInvite[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<{ email: string; role: Role } | null>(null);
  const [issued, setIssued] = useState<{ email: string; link: string; expiresAt: string } | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  async function load() {
    try {
      setInvites((await api.listMemberInvites()).invites);
      setLoadError(null);
    } catch (caught) {
      setLoadError(message(caught, 'Could not read the open invitations.'));
    }
  }

  useEffect(() => {
    void load();
  }, [changed]);

  async function invite() {
    if (form === null) return;
    if (form.email.trim() === '') {
      setError('Enter the address of the person to invite.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const made = await api.inviteMember(form.email.trim(), form.role);
      setIssued({ email: made.email, link: joinLink(made.code), expiresAt: made.expiresAt });
      setForm(null);
      await load();
    } catch (caught) {
      setError(message(caught, 'The invitation was not made.'));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    setBusy(true);
    setError(null);
    try {
      await api.revokeMemberInvite(id);
      setRevoking(null);
      await load();
    } catch (caught) {
      setError(message(caught, 'The invitation was not revoked.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="people-invites">
      <h3>Invitations</h3>
      {loadError && (
        <p role="alert" className="error">
          {loadError}
        </p>
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {issued && (
        <div className="notice" role="status">
          <p>
            Send this link to {issued.email}. It is shown only now, and it works once, until{' '}
            {formatTimestamp(issued.expiresAt)}.
          </p>
          <p>
            <code className="enrolment-secret">{issued.link}</code>
          </p>
          <button type="button" className="link" onClick={() => setIssued(null)}>
            Done
          </button>
        </div>
      )}

      {invites !== null && invites.length > 0 && (
        <ul className="history-list" aria-label="Open Invitations">
          {invites.map((open) => (
            <li key={open.id}>
              <p>
                <strong>{open.email}</strong> as {ROLE_LABELS[open.role]}
                <span className="muted">
                  {' '}
                  · invited {formatTimestamp(open.createdAt)}
                  {open.createdByEmail ? ` by ${open.createdByEmail}` : ''} · open until{' '}
                  {formatTimestamp(open.expiresAt)}
                </span>
              </p>
              {revoking === open.id ? (
                <div className="undo-form" role="group" aria-label={`Revoke the invitation to ${open.email}`}>
                  <p>Its link stops working at once. You can invite them again afterwards.</p>
                  <div className="row">
                    <button type="button" disabled={busy} onClick={() => void revoke(open.id)}>
                      {busy ? 'Revoking…' : 'Revoke'}
                    </button>
                    <button type="button" className="link" onClick={() => setRevoking(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="link"
                  aria-label={`Revoke the invitation to ${open.email}`}
                  onClick={() => setRevoking(open.id)}
                >
                  Revoke
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {invites !== null && invites.length === 0 && !issued && <p className="muted">No invitation is open.</p>}

      {form === null ? (
        <button
          type="button"
          onClick={() => {
            setError(null);
            setForm({ email: '', role: 'bookkeeper' });
          }}
        >
          Invite A Person
        </button>
      ) : (
        <>
          <p className="muted">The next step shows a link for you to send them. Nothing is emailed from here.</p>
          <div className="new-account" role="group" aria-label="Invite A Person">
            <label className="field">
              <span>Their Email Address</span>
              <input
                type="email"
                autoComplete="off"
                value={form.email}
                onChange={(event) => {
                  const email = event.target.value;
                  setForm((current) => (current === null ? current : { ...current, email }));
                }}
              />
            </label>
            <label className="field">
              <span>Role</span>
              <select
                value={form.role}
                onChange={(event) => {
                  const role = event.target.value as Role;
                  setForm((current) => (current === null ? current : { ...current, role }));
                }}
              >
                <option value="bookkeeper">Bookkeeper</option>
                <option value="viewer">Viewer</option>
                <option value="admin">Admin</option>
              </select>
            </label>
            <div className="row">
              <button type="button" disabled={busy} onClick={() => void invite()}>
                {busy ? 'Inviting…' : 'Make The Invitation'}
              </button>
              <button type="button" className="link" onClick={() => setForm(null)}>
                Cancel
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
